/**
 * 🎯 SIRALAMA TAKİP AJANI — elle girilen kelimelerin arama sonuçlarındaki konumu ve elle
 * girilen soruların AI motorlarındaki karşılığı tek ekranda.
 *
 * Dört sekme:
 *   SERP        → pozisyon, değişim, SERP özellikleri, kanibalizasyon, tahmini trafik
 *   AI Görünürlük → soru × motor ızgarası, anılma sırası, cevap kanıtı, marka sıralaması
 *   Rakipler    → aynı kelimelerde kimler önümüzde + AI cevaplarında kimler anılıyor
 *   Trend       → zaman içinde ortalama pozisyon ve AI anılma oranı
 */
import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useToast } from "@/hooks/use-toast";
import { confirmCost } from "@/components/cost-confirm";
import { GlPageHeader, GlSection, GlMetric, GlTabs, GlEmpty, GlButton } from "@/components/ui/gl-kit";
import { GlTableWrap, GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Target, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Minus, Sparkles, ExternalLink, Download,
  ChevronDown, ChevronRight, Search, Bot, Users, TrendingUp, AlertTriangle, Wand2,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

// ── Tipler (backend cevabının şekli) ─────────────────────────────────────────
type Feature = { type: string; owned: boolean };
type Rival = { domain: string; position: number; url: string; title: string };
type OwnUrl = { url: string; position: number };
type KwRow = {
  keywordId: number; keyword: string; market: string; device: string; tags: string[];
  volume: number; difficulty: number;
  position: number | null; prevPosition: number | null; url: string | null;
  serpFeatures: Feature[]; rivals: Rival[]; ownUrls: OwnUrl[];
  aiOverviewPresent: boolean; aiOverviewCited: boolean; aiOverviewSources: string[];
  checkedAt: string | null;
};
type Run = { promptId: number; engine: string; mentioned: boolean; rank: number | null; brands: string[]; citedDomains: string[]; answer: string };
type PromptRow = { id: number; prompt: string; market: string; source: string; runs: Run[] };
type Data = {
  configured: boolean;
  domain: { id: number; url: string };
  markets: { value: string; label: string }[];
  keywords: KwRow[];
  serpSummary: {
    tracked: number; ranked: number; top3: number; top10: number; top20: number;
    avgPosition: number; visibility: number;
    improved: number; declined: number; unchanged: number; lost: number; gained: number;
    estimatedTraffic: number;
    featureCoverage: { type: string; label: string; present: number; owned: number }[];
    cannibalized: { keyword: string; urls: OwnUrl[] }[];
    aiOverview: { present: number; cited: number };
    rivals: { domain: string; keywordsRanked: number; avgPosition: number; top10: number; aheadOfUs: number; visibility: number }[];
  };
  prompts: PromptRow[];
  aiSummary: {
    prompts: number; runs: number; visibility: number; mentionRate: number; sov: number; avgRank: number;
    byEngine: { engine: string; visibility: number; mentionRate: number; avgRank: number; runs: number }[];
    topBrands: { name: string; mentions: number; avgRank: number; isUs: boolean }[];
    citedDomains: { domain: string; count: number; isUs: boolean }[];
  };
  trend: { serp: { gun: string; ortPozisyon: number; sirali: number; toplam: number }[]; ai: { gun: string; anilmaOrani: number; toplam: number }[] };
};

const FEATURE_TR: Record<string, string> = {
  ai_overview: "AI Overview", featured_snippet: "Öne Çıkan", people_also_ask: "Bunlar da Soruldu",
  local_pack: "Yerel Paket", map: "Harita", video: "Video", images: "Görseller", shopping: "Alışveriş",
  top_stories: "Haberler", knowledge_graph: "Bilgi Paneli", related_searches: "İlgili Aramalar",
  discussions_and_forums: "Forum", carousel: "Karusel", paid: "Reklam", popular_products: "Popüler Ürünler",
};
type Tab = "serp" | "ai" | "rakip" | "trend";
const featTr = (t: string) => FEATURE_TR[t] ?? t.replace(/_/g, " ");
const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => u.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 52);

/** Pozisyon + bir önceki çalıştırmaya göre değişim oku */
function PositionCell({ pos, prev }: { pos: number | null; prev: number | null }) {
  if (pos == null) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <GlBadge color="light">100+</GlBadge>
        {prev != null && <span className="text-theme-xs text-error-500">kaybedildi</span>}
      </span>
    );
  }
  // Takip artık ilk 100 — 20 üstü de gerçek bir pozisyon, "light" ile ayrılır
  const color = pos <= 3 ? "success" : pos <= 10 ? "primary" : pos <= 20 ? "warning" : "light";
  const delta = prev == null ? null : prev - pos; // pozitif = yükseldik
  return (
    <span className="inline-flex items-center gap-1.5">
      <GlBadge color={color} variant="solid">{pos}</GlBadge>
      {delta == null ? (
        prev === null ? <span className="text-theme-xs text-brand-500">yeni</span> : null
      ) : delta > 0 ? (
        <span className="inline-flex items-center text-theme-xs font-medium text-success-600"><ArrowUp className="h-3 w-3" />{delta}</span>
      ) : delta < 0 ? (
        <span className="inline-flex items-center text-theme-xs font-medium text-error-600"><ArrowDown className="h-3 w-3" />{Math.abs(delta)}</span>
      ) : (
        <span className="inline-flex items-center text-theme-xs text-gray-400"><Minus className="h-3 w-3" /></span>
      )}
    </span>
  );
}

export default function RankTracking() {
  const { data: domains } = useListDomains();
  const [domainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>("serp");
  const [kwInput, setKwInput] = useState("");
  const [prInput, setPrInput] = useState("");
  const [market, setMarket] = useState("tr");
  const [device, setDevice] = useState("desktop");
  const [scope, setScope] = useState<"both" | "serp" | "ai">("both");
  const [busy, setBusy] = useState("");
  const [openKw, setOpenKw] = useState<number | null>(null);
  const [openPr, setOpenPr] = useState<number | null>(null);
  const [suggests, setSuggests] = useState<{ keyword: string; volume: number; position: number }[]>([]);
  const [prSuggests, setPrSuggests] = useState<string[]>([]);

  const { data, isLoading } = useQuery<Data>({
    queryKey: ["rank-tracking", domainId],
    enabled: !!domainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${domainId}/rank-tracking`);
      if (!r.ok) throw new Error("Veri alınamadı");
      return r.json();
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["rank-tracking", domainId] });

  const post = async (path: string, body: unknown) => {
    const r = await fetch(`/api/domains/${domainId}/rank-tracking${path}`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error ?? "İşlem başarısız");
    return j;
  };

  // ── Aksiyonlar
  const addKeywords = async () => {
    if (!kwInput.trim()) return;
    setBusy("kw");
    try {
      const j = await post("/keywords", { keywords: kwInput, market, device });
      toast({ title: `${j.added} kelime eklendi`, description: j.skipped ? `${j.skipped} kelime zaten listede.` : "Takibi çalıştırarak pozisyonları çekebilirsin." });
      setKwInput(""); refresh();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const addPrompts = async (text?: string) => {
    const val = text ?? prInput;
    if (!val.trim()) return;
    setBusy("pr");
    try {
      const j = await post("/prompts", { prompts: val, market });
      toast({ title: `${j.added} soru eklendi`, description: j.skipped ? `${j.skipped} soru zaten listede.` : "AI takibini çalıştırarak cevapları ölçebilirsin." });
      setPrInput(""); setPrSuggests([]); refresh();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const del = async (kind: "keywords" | "prompts", id: number) => {
    await fetch(`/api/domains/${domainId}/rank-tracking/${kind}/${id}`, { method: "DELETE" });
    refresh();
  };

  const run = async () => {
    if (!(await confirmCost("rank-tracking", scope === "serp" ? "Yalnız arama sonuçları taranacak." : scope === "ai" ? "Yalnız AI motorları sorgulanacak." : "Hem arama sonuçları hem AI motorları."))) return;
    setBusy("run");
    try {
      const j = await post("/run", { scope, includeAiOverview: true });
      toast({ title: "Takip tamamlandı", description: `${j.serpChecked} kelime tarandı, ${j.aiRuns} AI cevabı ölçüldü.${j.failed?.length ? ` ${j.failed.length} sorgu başarısız.` : ""}` });
      refresh();
    } catch (e) { toast({ title: "Takip hatası", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const suggestKw = async () => {
    if (!(await confirmCost("rank-suggest"))) return;
    setBusy("sug");
    try {
      const j = await post("/suggest-keywords", { market });
      setSuggests(j.suggestions ?? []);
      if (!j.suggestions?.length) toast({ title: "Öneri bulunamadı", description: "Bu site için sıralanan kelime verisi yok." });
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const suggestPr = async () => {
    setBusy("sugp");
    try {
      const j = await post("/suggest-prompts", {});
      setPrSuggests(j.suggestions ?? []);
      if (!j.suggestions?.length) toast({ title: "Öneri üretilemedi", description: "Önce birkaç anahtar kelime ekle." });
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(""); }
  };

  if (!domainId) {
    return <div className="p-4 md:p-6"><GlEmpty title="Önce bir site seç" desc="Soldaki menüden aktif siteyi seçtiğinde takip listesi açılır." icon={<Target className="h-6 w-6" />} /></div>;
  }

  const s = data?.serpSummary;
  const ai = data?.aiSummary;
  const engines = ai?.byEngine ?? [];

  return (
    <div className="mx-auto max-w-[1536px] p-4 md:p-6">
      <GlPageHeader
        title="Sıralama Takip Ajanı"
        desc="Takip etmek istediğin kelimeleri ve soruları sen gir — hem arama sonuçlarındaki sıranı hem AI cevaplarındaki görünürlüğünü rakiplerle birlikte ölç."
        icon={<Target className="h-5 w-5" />}
        actions={
          <>
            <Select value={scope} onValueChange={(v) => setScope(v as typeof scope)}>
              <SelectTrigger className="h-10 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Arama + AI</SelectItem>
                <SelectItem value="serp">Yalnız arama</SelectItem>
                <SelectItem value="ai">Yalnız AI</SelectItem>
              </SelectContent>
            </Select>
            <GlButton onClick={run} disabled={busy === "run"}>
              {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
              Takibi Çalıştır
            </GlButton>
          </>
        }
      />

      <div className="mb-5">
        <GlTabs<Tab>
          value={tab}
          onChange={setTab}
          items={[
            { key: "serp", label: `Arama Sıralaması${s?.tracked ? ` (${s.tracked})` : ""}` },
            { key: "ai", label: `AI Görünürlük${ai?.prompts ? ` (${ai.prompts})` : ""}` },
            { key: "rakip", label: "Rakipler" },
            { key: "trend", label: "Trend" },
          ]}
        />
      </div>

      {isLoading && <div className="flex items-center gap-2 text-theme-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>}

      {/* ── SERP SEKMESİ ─────────────────────────────────────────────────── */}
      {tab === "serp" && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <GlMetric label="Görünürlük" value={`%${s?.visibility ?? 0}`} hint="tıklama payı ağırlıklı" icon={<Search className="h-5 w-5" />} />
            <GlMetric label="Ortalama Sıra" value={s?.avgPosition || "—"} hint={`${s?.ranked ?? 0}/${s?.tracked ?? 0} kelime sıralanıyor`} />
            <GlMetric label="İlk 3" value={s?.top3 ?? 0} />
            <GlMetric label="İlk 10" value={s?.top10 ?? 0} />
            <GlMetric label="Tahmini Trafik" value={fmt(s?.estimatedTraffic ?? 0)} hint="aylık, sıraya göre" />
            <GlMetric label="AI Overview" value={`${s?.aiOverview.cited ?? 0}/${s?.aiOverview.present ?? 0}`} hint="kaynak gösterildik / çıktı" icon={<Bot className="h-5 w-5" />} />
          </div>

          {!!(s && (s.improved || s.declined || s.gained || s.lost)) && (
            <div className="flex flex-wrap items-center gap-2">
              <GlBadge color="success"><ArrowUp className="h-3 w-3" /> {s.improved} yükseldi</GlBadge>
              <GlBadge color="error"><ArrowDown className="h-3 w-3" /> {s.declined} düştü</GlBadge>
              <GlBadge color="light"><Minus className="h-3 w-3" /> {s.unchanged} sabit</GlBadge>
              {!!s.gained && <GlBadge color="primary">{s.gained} yeni giriş</GlBadge>}
              {!!s.lost && <GlBadge color="warning">{s.lost} kaybedildi</GlBadge>}
            </div>
          )}

          {/* Kelime ekleme */}
          <GlSection
            title="Takip Listesine Kelime Ekle"
            desc="Her satıra bir kelime yaz (virgülle de ayırabilirsin). Hacim ve zorluk otomatik çekilir."
            action={
              <GlButton variant="outline" size="sm" onClick={suggestKw} disabled={busy === "sug"}>
                {busy === "sug" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                Sitemden öner
              </GlButton>
            }
          >
            <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
              <textarea
                value={kwInput}
                onChange={(e) => setKwInput(e.target.value)}
                rows={3}
                placeholder={"böcek ilaçlama istanbul\nfare ilaçlama fiyatları"}
                className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-theme-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-brand-300 dark:border-gray-700 dark:bg-white/[0.03] dark:text-white/90"
              />
              <div className="flex flex-wrap items-start gap-2">
                <Select value={market} onValueChange={setMarket}>
                  <SelectTrigger className="h-10 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>{(data.markets ?? []).map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={device} onValueChange={setDevice}>
                  <SelectTrigger className="h-10 w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desktop">Masaüstü</SelectItem>
                    <SelectItem value="mobile">Mobil</SelectItem>
                  </SelectContent>
                </Select>
                <GlButton onClick={addKeywords} disabled={busy === "kw" || !kwInput.trim()}>
                  {busy === "kw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ekle
                </GlButton>
              </div>
            </div>

            {!!suggests.length && (
              <div className="mt-4 rounded-xl border border-brand-200 bg-brand-25 p-3 dark:border-brand-500/30 dark:bg-brand-500/[0.06]">
                <p className="mb-2 text-theme-xs font-medium text-gray-700 dark:text-gray-200">Sitenin hâlihazırda sıralandığı kelimeler — tıkla, kutuya eklensin:</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggests.map((sg) => (
                    <button
                      key={sg.keyword}
                      onClick={() => setKwInput((v) => (v ? `${v}\n${sg.keyword}` : sg.keyword))}
                      className="rounded-full border border-brand-200 bg-white px-2.5 py-1 text-theme-xs text-gray-700 hover:bg-brand-50 dark:border-brand-500/30 dark:bg-white/[0.04] dark:text-gray-200"
                    >
                      {sg.keyword} <span className="text-gray-400">· {fmt(sg.volume)} · {sg.position}.</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </GlSection>

          {/* Kanibalizasyon */}
          {!!s?.cannibalized.length && (
            <GlSection title="Kanibalizasyon Uyarısı" desc="Aynı kelimede birden fazla sayfan sıralanıyor — sayfalar birbirinin gücünü bölüyor.">
              <div className="space-y-3">
                {s.cannibalized.map((c) => (
                  <div key={c.keyword} className="rounded-xl border border-warning-200 bg-warning-50 p-3 dark:border-warning-500/30 dark:bg-warning-500/10">
                    <div className="mb-1.5 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-warning-600" />
                      <span className="text-theme-sm font-medium text-gray-800 dark:text-white/90">{c.keyword}</span>
                    </div>
                    <ul className="space-y-1">
                      {c.urls.map((u) => (
                        <li key={u.url} className="flex items-center gap-2 text-theme-xs text-gray-600 dark:text-gray-300">
                          <GlBadge color="warning">{u.position}</GlBadge>
                          <a href={u.url} target="_blank" rel="noreferrer" className="truncate hover:underline">{shortUrl(u.url)}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </GlSection>
          )}

          {/* Kelime tablosu */}
          <GlSection
            title="Takip Edilen Kelimeler"
            bodyClass="!px-0 !py-0"
            action={!!data.keywords.length && (
              <GlButton variant="outline" size="sm" onClick={() => {
                const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const rows = [
                  ["Kelime", "Pazar", "Cihaz", "Sıra", "Önceki", "URL", "Hacim", "Zorluk", "AI Overview", "AIO Kaynağıyız"],
                  ...data.keywords.map((k) => [k.keyword, k.market, k.device, k.position ?? "100+", k.prevPosition ?? "", k.url ?? "", k.volume, k.difficulty, k.aiOverviewPresent ? "var" : "yok", k.aiOverviewCited ? "evet" : "hayır"]),
                ];
                const csv = "﻿" + rows.map((r) => r.map(esc).join(";")).join("\n");
                const a = document.createElement("a");
                a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
                a.download = `siralama-takibi-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
              }}>
                <Download className="h-4 w-4" /> CSV
              </GlButton>
            )}
          >
            {!data.keywords.length ? (
              <div className="p-6"><GlEmpty title="Henüz kelime eklenmedi" desc="Yukarıdaki kutuya takip etmek istediğin kelimeleri yaz." icon={<Search className="h-6 w-6" />} /></div>
            ) : (
              <GlTableWrap>
                <GlTable>
                  <GlTHead>
                    <GlTr>
                      <GlTh>Kelime</GlTh>
                      <GlTh>Sıra</GlTh>
                      <GlTh>Sıralanan Sayfa</GlTh>
                      <GlTh align="right">Hacim</GlTh>
                      <GlTh align="right">Zorluk</GlTh>
                      <GlTh>SERP Özellikleri</GlTh>
                      <GlTh>Pazar</GlTh>
                      <GlTh />
                    </GlTr>
                  </GlTHead>
                  <GlTBody>
                    {data.keywords.map((k) => (
                      <Fragment key={k.keywordId}>
                        <GlTr onClick={() => setOpenKw(openKw === k.keywordId ? null : k.keywordId)}>
                          <GlTd>
                            <span className="inline-flex items-center gap-1.5 font-medium text-gray-800 dark:text-white/90">
                              {openKw === k.keywordId ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                              {k.keyword}
                            </span>
                          </GlTd>
                          <GlTd><PositionCell pos={k.position} prev={k.prevPosition} /></GlTd>
                          <GlTd>
                            {k.url ? (
                              <a href={k.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-theme-xs text-brand-500 hover:underline">
                                {shortUrl(k.url)} <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : <span className="text-theme-xs text-gray-400">—</span>}
                          </GlTd>
                          <GlTd align="right">{k.volume ? fmt(k.volume) : "—"}</GlTd>
                          <GlTd align="right">{k.difficulty || "—"}</GlTd>
                          <GlTd>
                            <span className="flex flex-wrap gap-1">
                              {k.aiOverviewPresent && <GlBadge color={k.aiOverviewCited ? "success" : "light"}>AI Overview{k.aiOverviewCited ? " ✓" : ""}</GlBadge>}
                              {k.serpFeatures.filter((f) => f.type !== "ai_overview").slice(0, 3).map((f) => (
                                <GlBadge key={f.type} color={f.owned ? "success" : "light"}>{featTr(f.type)}{f.owned ? " ✓" : ""}</GlBadge>
                              ))}
                              {k.serpFeatures.length > 4 && <GlBadge color="light">+{k.serpFeatures.length - 4}</GlBadge>}
                              {!k.serpFeatures.length && <span className="text-theme-xs text-gray-400">—</span>}
                            </span>
                          </GlTd>
                          <GlTd><span className="text-theme-xs uppercase text-gray-500">{k.market} · {k.device === "mobile" ? "mobil" : "masaüstü"}</span></GlTd>
                          <GlTd align="right">
                            <button onClick={(e) => { e.stopPropagation(); del("keywords", k.keywordId); }} className="text-gray-400 hover:text-error-500" title="Takipten çıkar">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </GlTd>
                        </GlTr>
                        {openKw === k.keywordId && (
                          <GlTr>
                            <GlTd colSpan={8} className="!py-4 bg-gray-25 dark:bg-white/[0.02]">
                              <div>
                                <div className="grid gap-5 lg:grid-cols-2">
                                  <div>
                                    <p className="mb-2 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">Bu kelimede önümüzdekiler</p>
                                    {k.rivals.length ? (
                                      <ul className="space-y-1.5">
                                        {k.rivals.slice(0, 8).map((r) => (
                                          <li key={r.url} className="flex items-center gap-2 text-theme-xs">
                                            <GlBadge color={k.position != null && r.position < k.position ? "error" : "light"}>{r.position}</GlBadge>
                                            <a href={r.url} target="_blank" rel="noreferrer" className="truncate text-gray-700 hover:underline dark:text-gray-300">{r.domain}</a>
                                            <span className="truncate text-gray-400">{r.title.slice(0, 48)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : <p className="text-theme-xs text-gray-400">Henüz tarama yapılmadı.</p>}
                                  </div>
                                  <div>
                                    <p className="mb-2 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">Tüm SERP özellikleri</p>
                                    <div className="mb-3 flex flex-wrap gap-1.5">
                                      {k.serpFeatures.length ? k.serpFeatures.map((f) => (
                                        <GlBadge key={f.type} color={f.owned ? "success" : "light"}>{featTr(f.type)}{f.owned ? " ✓ bizde" : ""}</GlBadge>
                                      )) : <span className="text-theme-xs text-gray-400">—</span>}
                                    </div>
                                    {!!k.aiOverviewSources.length && (
                                      <>
                                        <p className="mb-1.5 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">AI Overview kaynakları</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {k.aiOverviewSources.map((d) => <GlBadge key={d} color={data.domain.url.includes(d) ? "success" : "light"}>{d}</GlBadge>)}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </GlTd>
                          </GlTr>
                        )}
                      </Fragment>
                    ))}
                  </GlTBody>
                </GlTable>
              </GlTableWrap>
            )}
          </GlSection>

          {/* SERP özellik kapsamı */}
          {!!s?.featureCoverage.length && (
            <GlSection title="SERP Özellik Kapsamı" desc="Takip edilen kelimelerde hangi özellik kaç kez çıkıyor ve kaçını biz kapmışız.">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {s.featureCoverage.map((f) => (
                  <div key={f.type} className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-theme-sm font-medium text-gray-800 dark:text-white/90">{featTr(f.type)}</span>
                      <span className="text-theme-xs text-gray-500">{f.owned}/{f.present}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${f.present ? (f.owned / f.present) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </GlSection>
          )}
        </div>
      )}

      {/* ── AI SEKMESİ ───────────────────────────────────────────────────── */}
      {tab === "ai" && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <GlMetric label="AI Görünürlük" value={`%${ai?.visibility ?? 0}`} hint="sıra ağırlıklı" icon={<Bot className="h-5 w-5" />} />
            <GlMetric label="Anılma Oranı" value={`%${ai?.mentionRate ?? 0}`} hint={`${ai?.runs ?? 0} cevap ölçüldü`} />
            <GlMetric label="Anılma Payı" value={`%${ai?.sov ?? 0}`} hint="tüm marka anılmaları içinde" />
            <GlMetric label="Ortalama Sıra" value={ai?.avgRank || "—"} hint="cevapta kaçıncı marka" />
          </div>

          {!!engines.length && (
            <GlSection title="Motor Kırılımı" desc="Hangi AI motorunda güçlüyüz, hangisinde hiç görünmüyoruz.">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {engines.map((e) => (
                  <div key={e.engine} className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                    <p className="text-theme-sm font-medium text-gray-800 dark:text-white/90">{e.engine}</p>
                    <p className="mt-2 text-2xl font-bold text-gray-800 dark:text-white/90">%{e.visibility}</p>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${e.visibility}%` }} />
                    </div>
                    <p className="mt-2 text-theme-xs text-gray-500">%{e.mentionRate} anılma · {e.runs} cevap</p>
                  </div>
                ))}
              </div>
            </GlSection>
          )}

          <GlSection
            title="Takip Edilecek Soruları Ekle"
            desc="İnsanların yapay zekâya soracağı soruları yaz — her satır ayrı bir soru olarak takip edilir."
            action={
              <GlButton variant="outline" size="sm" onClick={suggestPr} disabled={busy === "sugp"}>
                {busy === "sugp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Kelimelerimden öner
              </GlButton>
            }
          >
            <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
              <textarea
                value={prInput}
                onChange={(e) => setPrInput(e.target.value)}
                rows={3}
                placeholder={"İstanbul'da böcek ilaçlama için hangi firmayı önerirsin?\nEv ilaçlaması yaptırırken nelere dikkat etmeliyim?"}
                className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-theme-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-brand-300 dark:border-gray-700 dark:bg-white/[0.03] dark:text-white/90"
              />
              <GlButton onClick={() => addPrompts()} disabled={busy === "pr" || !prInput.trim()}>
                {busy === "pr" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ekle
              </GlButton>
            </div>
            {!!prSuggests.length && (
              <div className="mt-4 rounded-xl border border-brand-200 bg-brand-25 p-3 dark:border-brand-500/30 dark:bg-brand-500/[0.06]">
                <p className="mb-2 text-theme-xs font-medium text-gray-700 dark:text-gray-200">Takip ettiğin kelimelerden türetilen sorular — tıkla, kutuya eklensin:</p>
                <div className="flex flex-wrap gap-1.5">
                  {prSuggests.map((p) => (
                    <button key={p} onClick={() => setPrInput((v) => (v ? `${v}\n${p}` : p))}
                      className="rounded-full border border-brand-200 bg-white px-2.5 py-1 text-left text-theme-xs text-gray-700 hover:bg-brand-50 dark:border-brand-500/30 dark:bg-white/[0.04] dark:text-gray-200">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </GlSection>

          <GlSection title="Soru × Motor Izgarası" desc="Her hücre: o motor o soruda markanı andı mı, kaçıncı sırada." bodyClass="!px-0 !py-0">
            {!data.prompts.length ? (
              <div className="p-6"><GlEmpty title="Henüz soru eklenmedi" desc="Yukarıdan takip etmek istediğin soruları ekle." icon={<Bot className="h-6 w-6" />} /></div>
            ) : (
              <GlTableWrap>
                <GlTable>
                  <GlTHead>
                    <GlTr>
                      <GlTh>Soru</GlTh>
                      {["ChatGPT", "Gemini", "Claude", "Perplexity", "AI Overview"].map((e) => <GlTh key={e} align="center">{e}</GlTh>)}
                      <GlTh />
                    </GlTr>
                  </GlTHead>
                  <GlTBody>
                    {data.prompts.map((p) => (
                      <Fragment key={p.id}>
                        <GlTr onClick={() => setOpenPr(openPr === p.id ? null : p.id)}>
                          <GlTd>
                            <span className="inline-flex items-start gap-1.5 font-medium text-gray-800 dark:text-white/90">
                              {openPr === p.id ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />}
                              <span className="max-w-md">{p.prompt}</span>
                            </span>
                          </GlTd>
                          {["ChatGPT", "Gemini", "Claude", "Perplexity", "AI Overview"].map((eng) => {
                            const r = p.runs.find((x) => x.engine === eng);
                            return (
                              <GlTd key={eng} align="center">
                                {!r ? <span className="text-theme-xs text-gray-300">—</span>
                                  : r.mentioned ? <GlBadge color="success" variant="solid">{r.rank ?? "✓"}</GlBadge>
                                  : <GlBadge color="light">yok</GlBadge>}
                              </GlTd>
                            );
                          })}
                          <GlTd align="right">
                            <button onClick={(e) => { e.stopPropagation(); del("prompts", p.id); }} className="text-gray-400 hover:text-error-500" title="Takipten çıkar">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </GlTd>
                        </GlTr>
                        {openPr === p.id && (
                          <GlTr>
                            <GlTd colSpan={7} className="!py-4 bg-gray-25 dark:bg-white/[0.02]">
                              <div className="space-y-3">
                                {p.runs.length ? p.runs.map((r) => (
                                  <div key={r.engine} className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
                                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                      <span className="text-theme-sm font-semibold text-gray-800 dark:text-white/90">{r.engine}</span>
                                      {r.mentioned ? <GlBadge color="success">{r.rank}. sırada anıldı</GlBadge> : <GlBadge color="light">anılmadı</GlBadge>}
                                      {r.brands.slice(0, 6).map((b, i) => (
                                        <GlBadge key={b + i} color={r.rank === i + 1 ? "primary" : "light"} variant={r.rank === i + 1 ? "solid" : "light"}>{b}</GlBadge>
                                      ))}
                                    </div>
                                    <p className="whitespace-pre-wrap text-theme-xs leading-relaxed text-gray-600 dark:text-gray-300">{r.answer.slice(0, 900)}{r.answer.length > 900 ? "…" : ""}</p>
                                  </div>
                                )) : <p className="text-theme-xs text-gray-400">Bu soru için henüz takip çalıştırılmadı.</p>}
                              </div>
                            </GlTd>
                          </GlTr>
                        )}
                      </Fragment>
                    ))}
                  </GlTBody>
                </GlTable>
              </GlTableWrap>
            )}
          </GlSection>
        </div>
      )}

      {/* ── RAKİPLER SEKMESİ ─────────────────────────────────────────────── */}
      {tab === "rakip" && data && (
        <div className="grid gap-5 xl:grid-cols-2">
          <GlSection title="Arama Sonuçlarındaki Rakipler" desc="Takip ettiğin kelimelerde en çok görünen siteler." bodyClass="!px-0 !py-0">
            {!s?.rivals.length ? (
              <div className="p-6"><GlEmpty title="Rakip verisi yok" desc="Kelime ekleyip takibi çalıştırdığında rakipler otomatik çıkar." icon={<Users className="h-6 w-6" />} /></div>
            ) : (
              <GlTableWrap>
                <GlTable>
                  <GlTHead><GlTr><GlTh>Site</GlTh><GlTh align="right">Görünürlük</GlTh><GlTh align="right">Kelime</GlTh><GlTh align="right">Ort. Sıra</GlTh><GlTh align="right">İlk 10</GlTh><GlTh align="right">Bizden Önde</GlTh></GlTr></GlTHead>
                  <GlTBody>
                    {s.rivals.map((r) => (
                      <GlTr key={r.domain}>
                        <GlTd><span className="font-medium text-gray-800 dark:text-white/90">{r.domain}</span></GlTd>
                        <GlTd align="right">%{r.visibility}</GlTd>
                        <GlTd align="right">{r.keywordsRanked}</GlTd>
                        <GlTd align="right">{r.avgPosition}</GlTd>
                        <GlTd align="right">{r.top10}</GlTd>
                        <GlTd align="right">{r.aheadOfUs ? <GlBadge color="error">{r.aheadOfUs}</GlBadge> : <GlBadge color="success">0</GlBadge>}</GlTd>
                      </GlTr>
                    ))}
                  </GlTBody>
                </GlTable>
              </GlTableWrap>
            )}
          </GlSection>

          <div className="space-y-5">
            <GlSection title="AI Cevaplarında Anılan Markalar" desc="Yapay zekâ hangi markaları öneriyor — seninki nerede?" bodyClass="!px-0 !py-0">
              {!ai?.topBrands.length ? (
                <div className="p-6"><GlEmpty title="AI verisi yok" desc="Soru ekleyip AI takibini çalıştır." icon={<Bot className="h-6 w-6" />} /></div>
              ) : (
                <GlTableWrap>
                  <GlTable>
                    <GlTHead><GlTr><GlTh>#</GlTh><GlTh>Marka</GlTh><GlTh align="right">Anılma</GlTh><GlTh align="right">Ort. Sıra</GlTh></GlTr></GlTHead>
                    <GlTBody>
                      {ai.topBrands.map((b, i) => (
                        <GlTr key={b.name + i}>
                          <GlTd>{i + 1}</GlTd>
                          <GlTd>
                            <span className={`font-medium ${b.isUs ? "text-brand-500" : "text-gray-800 dark:text-white/90"}`}>
                              {b.name}{b.isUs && <GlBadge color="primary" className="ml-2">sen</GlBadge>}
                            </span>
                          </GlTd>
                          <GlTd align="right">{b.mentions}</GlTd>
                          <GlTd align="right">{b.avgRank}</GlTd>
                        </GlTr>
                      ))}
                    </GlTBody>
                  </GlTable>
                </GlTableWrap>
              )}
            </GlSection>

            {!!ai?.citedDomains.length && (
              <GlSection title="AI'ın Kaynak Gösterdiği Siteler" desc="AI Overview cevaplarında atıf verilen adresler.">
                <div className="flex flex-wrap gap-1.5">
                  {ai.citedDomains.map((c) => (
                    <GlBadge key={c.domain} color={c.isUs ? "success" : "light"}>{c.domain} · {c.count}</GlBadge>
                  ))}
                </div>
              </GlSection>
            )}
          </div>
        </div>
      )}

      {/* ── TREND SEKMESİ ────────────────────────────────────────────────── */}
      {tab === "trend" && data && (
        <div className="grid gap-5 xl:grid-cols-2">
          <GlSection title="Ortalama Arama Sırası" desc="Aşağı doğru inen çizgi iyileşmedir (1. sıraya yaklaşıyoruz).">
            {data.trend.serp.length < 2 ? (
              <GlEmpty title="Trend için en az iki çalıştırma gerekli" desc="Takibi farklı günlerde çalıştırdıkça grafik dolar." icon={<TrendingUp className="h-6 w-6" />} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend.serp}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                  <XAxis dataKey="gun" tick={{ fontSize: 11 }} />
                  <YAxis reversed domain={[1, 20]} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v: number) => [`${v}. sıra`, "Ortalama"]} />
                  <Line type="monotone" dataKey="ortPozisyon" stroke="#e83e3c" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </GlSection>

          <GlSection title="AI Anılma Oranı" desc="AI cevaplarının yüzde kaçında markan geçiyor.">
            {data.trend.ai.length < 2 ? (
              <GlEmpty title="Trend için en az iki çalıştırma gerekli" desc="AI takibini farklı günlerde çalıştırdıkça grafik dolar." icon={<Bot className="h-6 w-6" />} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend.ai}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                  <XAxis dataKey="gun" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v: number) => [`%${v}`, "Anılma"]} />
                  <Line type="monotone" dataKey="anilmaOrani" stroke="#e83e3c" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </GlSection>
        </div>
      )}
    </div>
  );
}
